import {
  renderGrid,
  renderXAxisLabels,
  renderYaxisNumerical,
  renderVerticalStripDividers,
} from '../../zoompan-canvas/renderAxes';
import {
  getBoundData,
  TpAspectBinding,
  TpBoundData,
  TpChartConfigParams,
  TpChartDataBinding,
} from '../chartDataBindings';
import {
  createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
  TpZoomPanelCoordMapper,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTableColumn, VALTYPES } from '../../../../data/interfaces';
import { getStandardDataColor } from '../../../themes/themes';
import { renderLegendItems, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import {
  createColorMappingInfo,
  getMappedColorLegendInfo,
  getMappedColorCategorical,
} from '../../helpers/colorMappingInfo';
import { chartDims, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getThemedGrayFraction } from '../../../themes/themes';
import { getLabelSpaceSize } from '../../helpers/configParams';
import { TpHoverInfo, TpHoverRenderContext } from '../../zoompan-canvas/ZoomPanCanvas';
import { getUserError } from '../../../../components/miscUtils';
import {
  calculateKernelDF,
  renderKernelDFCenterAndWhiskers,
  TpRenderKernelContext,
  TpKernelDFData,
} from '../../helpers/kernelDF';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';
import { clipToRenderedRange } from '../../zoompan-canvas/helpers';
import { calculateCenter, calculateWhiskers } from './rendererOptions';

// Data structure representing the information about the coordinates for a specific category (or globally if no color aspect is used)
// within a single coordinate axis (e.g. for a single property/column)
interface TpCoordinateCategoryInfo {
  columnInfo: TpDataTableColumn; // Pointer to the column info for the underlying coordinate
  coordinateIdx: number; // Index of the coordinate axis (e.g. which property/column it is)
  data: number[]; // Original values for the coordinate (not aggregated)
  dataSelected: number[]; // Original values for the coordinate that are selected
  density: TpKernelDFData;
  densitySelected: TpKernelDFData;
}

// Data structure for each series of aggregated values
// (e.g. for each category, or one global series if no color aspect is used)
interface TpAggregateInfo {
  coordinates: TpCoordinateCategoryInfo[];
  items: number[]; // List of row numbers of the items that belong to this aggregate group (e.g. category)
  selectedItems: number[]; // List of row numbers of the items that belong to this aggregate group and are selected
  category: string;
}

interface TpCoordinateInfo {
  columnInfo: TpDataTableColumn; // Pointer to the column info for the underlying coordinate
  minVal: number; // Used for scaling the axis
  maxVal: number; // Used for scaling the axis
}

// In case no color aspect is used, we still want to have a single "global" aggregate to render.
const DUMMY_CATEGORY = 'Global';

// Base size for the points, which is then multiplied by the pointSizeFactor from the config.
const BASE_POINT_SIZE = 3;

// Base gap in pixels between points of different categories on the same coordinate axis when scatterXAxis is enabled,
// which is then converted to logical coordinates based on the chart dimensions.
// Experimentally, this is large enough to prevent overlap for larger point sizes, while still not being too wide for smaller point sizes.
const BASE_GAP = 20;

/**
 * Get a map of all data, grouped by category (or a single global group if no color aspect is used).
 */
function gatherAggregateGroups(boundData: TpBoundData, categoriesAspect: TpAspectBinding | null) {
  const dataItemId = boundData.bindings.itemId.data;
  const hasCategories = categoriesAspect && categoriesAspect.data;
  const categoryGroups = new Map<string, TpAggregateInfo>();
  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  for (let itemNr = 0; itemNr < dataItemId.length; itemNr++) {
    const category = hasCategories ? categoriesAspect.data[itemNr] : DUMMY_CATEGORY;
    if (category == null) continue;

    if (!categoryGroups.has(category)) {
      categoryGroups.set(category, { category, coordinates: [], items: [], selectedItems: [] });
    }
    const group = categoryGroups.get(category)!;
    group.items.push(itemNr);
    if (isSelectedItem(selectedItemsMap, dataItemId[itemNr])) {
      group.selectedItems.push(itemNr);
    }
  }
  return categoryGroups;
}

/**
 * Calculate the min and max values for a list of numerical values, ignoring NaNs.
 */
function calcMinMax(values: number[]) {
  let minVal = Number.MAX_VALUE;
  let maxVal = -Number.MAX_VALUE;
  for (const val of values) {
    if (!isNaN(val)) {
      if (val < minVal) minVal = val;
      if (val > maxVal) maxVal = val;
    }
  }
  return { minVal, maxVal };
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const title = generateChartTitle(dataContext, dataBinding, config, `Coordinates profile`);

  // Coordinates
  const coordinateAspects = boundData.compoundBindings.coordinates;
  const categoriesAspect = boundData.bindings.categories;
  const hasCategories = categoriesAspect != null; // Check vs null and undefined.
  const categoryValues = categoriesAspect?.columnInfo.augmentedData.categories?.map((cat) => cat.value) ?? [
    DUMMY_CATEGORY,
  ];

  // Color and categories
  const opacity = config.opacity;
  const unselectedOpacity = config.unselectedOpacity;
  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'categories', opacity, globalColorMappingConfig);

  // Display type for the aggregated values.
  const displayType = config.displayType;
  const kernelSizeFactor = 0.2; // Placeholder for future config option
  const getKernelSize = (minVal: number, maxVal: number) => (maxVal - minVal) * kernelSizeFactor ** 2 * 0.25;
  const densityGammaFactor = 10; // Placeholder for future config option
  const gammaFactor = (densityGammaFactor / 10) ** 3 / 1 ** 3;

  // Other configs and constants that are placeholders for future config options:
  const pointSizeFactor = config.pointSizeFactor;

  // Gather the data for each coordinate and category:
  const aggregates: Map<string, TpAggregateInfo> = gatherAggregateGroups(boundData, categoriesAspect);
  const coordinates: TpCoordinateInfo[] = [];
  coordinateAspects.forEach((coordInp, coordIdx) => {
    if (coordInp.columnInfo.valueType !== VALTYPES.NUMBER)
      throw getUserError(`Coordinate should be numerical: ${coordInp.columnInfo.id}`);

    const { minVal, maxVal } = calcMinMax(coordInp.data);
    coordinates.push({ minVal, maxVal, columnInfo: coordInp.columnInfo });
    // Calculate category-specific metrics for the current column
    for (const [category, group] of aggregates.entries()) {
      const allVals = group.items.map((idx) => coordInp.data[idx]).filter((val) => !isNaN(val));
      const selectedVals = group.selectedItems.map((idx) => coordInp.data[idx]).filter((val) => !isNaN(val));
      group.coordinates.push({
        columnInfo: coordInp.columnInfo,
        coordinateIdx: coordIdx,
        data: allVals,
        dataSelected: selectedVals,
        density: calculateKernelDF(allVals, getKernelSize(minVal, maxVal), gammaFactor),
        densitySelected: calculateKernelDF(selectedVals, getKernelSize(minVal, maxVal), gammaFactor),
      });
    }
  });

  const globalMinVal = Math.min(...coordinates.map((coord) => coord.minVal));
  const globalMaxVal = Math.max(...coordinates.map((coord) => coord.maxVal));

  const hasLegend = colorMappingInfo.hasColors;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const xAxisHeight = getLabelSpaceSize(config.labelSpace);

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const range = globalMaxVal - globalMinVal;
  const zpLogicalRangeY = createLogicalRange(globalMinVal - range * 0.05, globalMaxVal + range * 0.05);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.yAxisWidth),
    RenderMaxDiffAbs(padding + 0),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
  );

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(
      renderContext,
      coordinates.map((coord) => coord.columnInfo.name),
    );
  }

  /**
   * Get the X coordinate for a point based on the config.
   * @param coordinateIdx Index of the coordinate axis (e.g. which property/column it is)
   * @param category Category of the point (used for jittering if scatterXAxis is enabled)
   * @param gap Amount of separation between categories in scatter mode, within the logic coordinates (0-1) axis.
   * @returns Logical X coordinate between 0 and 1
   */
  function getXAxisCenter(coordinateIdx: number, category: string, gap: number = 0.01) {
    const coordinateCenter = coordinateIdx + 0.5;

    if (!config.scatterXAxis || categoryValues.length <= 1) {
      return coordinateCenter / coordinates.length;
    }

    const categoryIdx = categoryValues.findIndex((cat) => cat === category);
    if (categoryIdx === -1) return coordinateCenter / coordinates.length;

    const categoryCount = categoryValues.length;

    // The logical width within a coordinate is 1.
    const MARGIN = 0.2; // Margin between coordinates, same as strip plot.
    const totalCategoryWidth = 1 - 2 * MARGIN;
    const baseGap = gap * coordinates.length; // Gap between categories

    if (baseGap * (categoryCount - 1) > totalCategoryWidth) {
      // If we have more categories than can fit with the requested gap,
      // the gap is set to a fraction.
      const adjustedGap = totalCategoryWidth / (categoryCount - 1);
      return (coordinateIdx + MARGIN + categoryIdx * adjustedGap) / coordinates.length;
    } else {
      // Center the categories around the coordinate center, and separate them by the requested gap.
      const totalCategoriesWidth = baseGap * (categoryCount - 1);
      const startX = coordinateCenter - totalCategoriesWidth / 2;
      return (startX + categoryIdx * baseGap) / coordinates.length;
    }
  }

  /** Get a gap value in logical coordinates (0-1) that corresponds to a requested gap in pixels,
   * based on the chart context (e.g. dimensions).
   *
   * Does not take into account the point size itself, which could be added in the future if needed.
   * @param mapX Function that maps from logical coordinates to render coordinates.
   * @param mapXInv Function that maps from render coordinates to logical coordinates.
   * @param pixelConv Function that converts a size in pixels to the corresponding
   * size in render coordinates (taking into account pixel ratio and zoom level).
   * @returns Gap value in logical coordinates (0-1) that corresponds to the requested pixel gap.
   */
  function mapPixelGapToLogicalGap(
    mapX: (coord: number) => number,
    mapXInv: (coord: number) => number,
    pixelConv: (size: number) => number,
  ): number {
    const logicalGap = mapXInv(mapX(1) + pixelConv(BASE_GAP)) - 1;
    return logicalGap;
  }

  const standardFillColor = getStandardDataColor().opacity(opacity);
  const grayColor = getThemedGrayFraction(0.5).opacity(opacity * unselectedOpacity);
  const grayColorFill = getThemedGrayFraction(0.5).opacity(opacity * unselectedOpacity * 0.5);

  function renderLines(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, mapinvx, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    // Gap between categories in scatter mode.
    const logicalGap = mapPixelGapToLogicalGap(mapx, mapinvx, pixelConv);

    renderGrid(renderContext, false, true);
    if (config.scatterXAxis) {
      renderVerticalStripDividers(coordinates.length, renderContext);
    }

    // First draw all unselected to make sure the selected ones are always on top and not hidden by the unselected ones.
    for (const isSelected of [false, true]) {
      for (const categoryId of categoryValues) {
        const aggregate = aggregates.get(categoryId);
        if (!aggregate) continue; // This should not happen, but just in case.
        let firstPoint = true;
        let fillStyle = standardFillColor;
        let strokeStyle = standardFillColor;

        if (!isSelected) {
          fillStyle = grayColorFill;
          strokeStyle = grayColor;
        } else if (colorMappingInfo.hasColors) {
          const colorStr = getMappedColorCategorical(colorMappingInfo, categoryId);
          fillStyle = colorStr;
          strokeStyle = colorStr;
        }
        ctx.fillStyle = fillStyle.toCanvasString();
        ctx.strokeStyle = strokeStyle.toCanvasString();
        ctx.lineWidth = isSelected ? renderContext.pixelRatio * 0.75 : renderContext.pixelRatio * 0.5;

        if (config.categoriesConnect === 'always') {
          ctx.beginPath();
          for (const [coordIdx, coordinateData] of aggregate.coordinates.entries()) {
            const kernel = isSelected ? coordinateData.densitySelected : coordinateData.density;
            const value = calculateCenter(kernel, displayType);
            if (!isNaN(value)) {
              const scr_x = mapx(getXAxisCenter(coordIdx, categoryId, logicalGap));
              const scr_y = mapy(value);
              if (firstPoint) ctx.moveTo(scr_x, scr_y);
              else ctx.lineTo(scr_x, scr_y);
              firstPoint = false;
            } else firstPoint = true;
          }
          ctx.stroke();
        }

        for (const [coordIdx, coordinateData] of aggregate.coordinates.entries()) {
          const kernel = isSelected ? coordinateData.densitySelected : coordinateData.density;
          const pointSize = BASE_POINT_SIZE;
          const centerX = getXAxisCenter(coordIdx, categoryId, logicalGap);
          const centerY = calculateCenter(kernel, displayType);
          const { lower, upper } = calculateWhiskers(kernel, config.whiskers);
          renderKernelDFCenterAndWhiskers(
            renderContext,
            strokeStyle,
            fillStyle,
            centerX,
            pointSize * pointSizeFactor,
            centerY,
            lower,
            upper,
          );
        }
      }
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (colorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    renderLegendItems(renderContext, legendItems);
  }

  function customHoverRenderer(
    hoverInfo: TpHoverInfo,
    hoverContext: TpHoverRenderContext,
    ctx: CanvasRenderingContext2D,
    mapX: TpZoomPanelCoordMapper,
    mapY: TpZoomPanelCoordMapper,
    pixelRatio: number,
  ) {
    clipToRenderedRange(ctx, mapX, mapY);

    // Gap between categories in scatter mode.
    const logicalGap = mapPixelGapToLogicalGap(mapX.map, mapX.mapInv, (size) => size * pixelRatio);

    const categoryId = hoverInfo.custom?.categoryId;
    const aggregate = aggregates.get(hoverInfo.custom?.categoryId);
    const isSelected = hoverInfo.custom?.isSelected;
    if (!aggregate || isSelected == null) return;

    let firstPoint = true;
    let fillStyle = standardFillColor;
    let strokeStyle = standardFillColor;

    if (!isSelected) {
      fillStyle = grayColorFill;
      strokeStyle = grayColor;
    } else if (colorMappingInfo.hasColors) {
      const categoryColor = getMappedColorCategorical(colorMappingInfo, categoryId);
      fillStyle = categoryColor;
      strokeStyle = categoryColor;
    }
    ctx.fillStyle = fillStyle.toCanvasString();
    ctx.strokeStyle = strokeStyle.toCanvasString();
    ctx.lineWidth = isSelected ? pixelRatio * 0.75 * 5 : pixelRatio * 0.5 * 5;

    if (config.categoriesConnect !== 'never') {
      ctx.beginPath();
      for (const [coordIdx, coordinateData] of aggregate.coordinates.entries()) {
        const kernel = isSelected ? coordinateData.densitySelected : coordinateData.density;
        const value = calculateCenter(kernel, displayType);
        if (!isNaN(value)) {
          const scr_x = mapX.map(getXAxisCenter(coordIdx, categoryId, logicalGap));
          const scr_y = mapY.map(value);
          if (firstPoint) ctx.moveTo(scr_x, scr_y);
          else ctx.lineTo(scr_x, scr_y);
          firstPoint = false;
        } else firstPoint = true;
      }
      ctx.stroke();
    }

    for (const [coordIdx, coordinateData] of aggregate.coordinates.entries()) {
      const kernel = isSelected ? coordinateData.densitySelected : coordinateData.density;
      const pointSize = 6;
      const centerX = getXAxisCenter(coordIdx, categoryId, logicalGap);
      const renderContext: TpRenderKernelContext = {
        canvasContext: ctx,
        mapx: mapX.map,
        mapy: mapY.map,
        pixelConv: (size) => size * pixelRatio,
      };
      const centerY = calculateCenter(kernel, displayType);
      const { lower, upper } = calculateWhiskers(kernel, config.whiskers);
      renderKernelDFCenterAndWhiskers(
        renderContext,
        strokeStyle,
        fillStyle,
        centerX,
        pointSize * pointSizeFactor,
        centerY,
        lower,
        upper,
      );
    }

    ctx.restore();
  }

  const areaLines: TpChartAreaDefinition = {
    id: 'lines',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'y',
    render: renderLines,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaLines,
    {
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },
  ];

  chartAreas.push({
    id: 'yaxis',
    logicalRangeX: createLogicalRange(0, 1),
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + chartDims.yAxisWidth)), // offset of Y=4 to make axes overlap
    renderRangeY: zpRenderRangeY,
    zoomPanY: true,
    render: renderYaxisNumerical,
  });

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaLines,
    aggregates,
    coordinates,
    hasCategories,
    categoriesColInfo: categoriesAspect?.columnInfo,
    displayType,
    customHoverRenderer: customHoverRenderer,
    title,
    getXAxisCenter,
    mapPixelGapToLogicalGap,
  };
}
